/**
 * Store Adapter
 * Provides adapter hooks and functions for bridging MobX store data to component props
 */

import { adaptResource, adaptResources, type WebAppResource } from './asset-adapter';
import { adaptFolder, adaptFolders, adaptFoldersToTree, type WebAppFolder } from './folder-adapter';
import { adaptPresets, adaptPresetsWithCategories, type WebAppPreset } from './preset-adapter';

import type { PaginationInfo } from '../components/AssetBrowser/AssetBrowser.types';
import type { TreeFolder } from '../components/FolderTree/FolderTree.types';
import type { Asset, Folder } from '../types';

// Re-export for convenience
export { adaptResource, adaptResources } from './asset-adapter';
export { adaptFolder, adaptFolders, adaptFoldersToTree, buildFolderTree } from './folder-adapter';
export { adaptPreset, adaptPresets, adaptPresetsWithCategories } from './preset-adapter';

/**
 * Web-app gridData structure (from appstore.js)
 */
export interface WebAppGridData {
  folder?: WebAppFolder[];
  resource?: WebAppResource[];
  parentFolder?: WebAppFolder;
  currentFolder?: WebAppFolder;
  pagination?: {
    count?: number;
    pageNumber?: number;
    pageSize?: number;
  };
}

/**
 * Web-app appStore structure (relevant fields)
 */
export interface WebAppAppStore {
  gridData?: WebAppGridData;
  folderData?: WebAppFolder[];
  currentFolderId?: string;
  expandedFolderIds?: string[];
  loading?: boolean;
  isLoading?: boolean;
  globalPresets?: WebAppPreset[];
  pageCounter?: number;
}

/**
 * Web-app authStore structure (relevant fields)
 */
export interface WebAppAuthStore {
  isAuthenticated?: boolean;
  user?: {
    _id?: string;
    name?: string;
  };
}

/**
 * Props returned for AssetBrowser component from store adapter
 */
export interface AdaptedAssetBrowserProps {
  folders: Folder[];
  assets: Asset[];
  loading: boolean;
  pagination: PaginationInfo;
  currentFolder?: Folder;
  parentFolder?: Folder;
}

/**
 * Props returned for FolderTree component from store adapter
 */
export interface AdaptedFolderTreeProps {
  folders: TreeFolder[];
  selectedId: string | null;
  expandedIds: string[];
}

/**
 * Calculate pagination info from web-app pagination data
 */
function calculatePagination(paginationData: { count?: number; pageNumber?: number; pageSize?: number }): PaginationInfo {
  const { count = 0, pageNumber = 1, pageSize = 20 } = paginationData;
  const totalPages = Math.ceil(count / pageSize);
  
  return {
    currentPage: pageNumber,
    totalPages,
    totalItems: count,
    itemsPerPage: pageSize,
  };
}

/**
 * Extract asset browser data from appStore and authStore
 * 
 * @param appStore - MobX appStore instance from web-app
 * @param _authStore - MobX authStore instance from web-app (reserved for future use)
 * @returns Props compatible with new asset browser components
 * 
 * @example
 * ```typescript
 * import { appStore, authStore } from '../stores';
 * import { getAssetBrowserProps } from '@intelligencebank/integration-components/adapters';
 * 
 * const AssetBrowser = observer(() => {
 *   const props = getAssetBrowserProps(appStore, authStore);
 *   return <NewAssetBrowser {...props} />;
 * });
 * ```
 */
export function getAssetBrowserProps(
  appStore: WebAppAppStore,
  _authStore?: WebAppAuthStore
): AdaptedAssetBrowserProps {
  const gridData = appStore.gridData || {};
  const folders = adaptFolders(gridData.folder || []);
  const assets = adaptResources(gridData.resource || []);
  
  const paginationData = gridData.pagination || {};
  const pagination = calculatePagination(paginationData);

  const loading = appStore.loading || appStore.isLoading || false;

  const currentFolder = gridData.currentFolder 
    ? adaptFolder(gridData.currentFolder) 
    : undefined;
    
  const parentFolder = gridData.parentFolder 
    ? adaptFolder(gridData.parentFolder) 
    : undefined;

  return {
    folders,
    assets,
    loading,
    pagination,
    currentFolder,
    parentFolder,
  };
}

/**
 * Extract folder tree data from appStore
 * 
 * @param appStore - MobX appStore instance from web-app
 * @returns Props compatible with FolderTree component
 * 
 * @example
 * ```typescript
 * import { appStore } from '../stores';
 * import { getFolderTreeProps } from '@intelligencebank/integration-components/adapters';
 * 
 * const FolderTreeView = observer(() => {
 *   const props = getFolderTreeProps(appStore);
 *   return <FolderTree {...props} />;
 * });
 * ```
 */
export function getFolderTreeProps(appStore: WebAppAppStore): AdaptedFolderTreeProps {
  const folders = adaptFoldersToTree(appStore.folderData || []);
  const selectedId = appStore.currentFolderId || null;
  const expandedIds = appStore.expandedFolderIds || [];

  return {
    folders,
    selectedId,
    expandedIds,
  };
}

/**
 * Extract pagination info from appStore
 * 
 * @param appStore - MobX appStore instance
 * @returns PaginationInfo object
 */
export function getPaginationInfo(appStore: WebAppAppStore): PaginationInfo {
  const paginationData = appStore.gridData?.pagination || {};
  return calculatePagination(paginationData);
}

/**
 * Extract a single folder from gridData
 * 
 * @param appStore - MobX appStore instance
 * @param index - Index of the folder in gridData.folder array
 * @returns Folder object or undefined if not found
 */
export function getFolderByIndex(appStore: WebAppAppStore, index: number): Folder | undefined {
  const folders = appStore.gridData?.folder || [];
  const folder = folders[index];
  return folder ? adaptFolder(folder) : undefined;
}

/**
 * Extract a single asset from gridData
 * 
 * @param appStore - MobX appStore instance
 * @param index - Index of the resource in gridData.resource array
 * @returns Asset object or undefined if not found
 */
export function getAssetByIndex(appStore: WebAppAppStore, index: number): Asset | undefined {
  const resources = appStore.gridData?.resource || [];
  const resource = resources[index];
  return resource ? adaptResource(resource) : undefined;
}

/**
 * Get the currently selected/active folder
 * 
 * @param appStore - MobX appStore instance
 * @returns The current folder or undefined
 */
export function getCurrentFolder(appStore: WebAppAppStore): Folder | undefined {
  const currentFolder = appStore.gridData?.currentFolder;
  return currentFolder ? adaptFolder(currentFolder) : undefined;
}

/**
 * Get presets with categories from appStore
 * 
 * @param appStore - MobX appStore instance
 * @param singlePresets - Asset-specific presets
 * @returns Categorized preset array
 */
export function getPresetCategories(
  appStore: WebAppAppStore,
  singlePresets: WebAppPreset[] = []
) {
  const globalPresets = appStore.globalPresets || [];
  return adaptPresetsWithCategories(singlePresets, globalPresets);
}

/**
 * Get flat list of presets from appStore
 * 
 * @param appStore - MobX appStore instance
 * @param singlePresets - Asset-specific presets
 * @returns Flat preset array
 */
export function getPresetOptions(
  appStore: WebAppAppStore,
  singlePresets: WebAppPreset[] = []
) {
  const globalPresets = appStore.globalPresets || [];
  return adaptPresets(singlePresets, globalPresets);
}